import { useMemo, useRef, useState } from 'react';
import { Dialog as D } from 'radix-ui';
import { motion } from 'motion/react';
import { Pin, PinOff, Search, X } from 'lucide-react';
import { cn } from '@nw/ui';
import { toast, usePreferences, useUpdatePreferences } from '@nw/web-platform';
import type { Space } from './spaces.js';

function StatusLine({ space }: { space: Space }) {
  const status = space.module?.useStatus?.() ?? null;
  return <span className="nw-tile-status">{status ?? space.description ?? ' '}</span>;
}

/** A space as a tile: its scene colour, icon, name and a live line. Hold to pin it to the scrubber. */
function SpaceTile({ space, current, pinned, onGo, onPin }: { space: Space; current: boolean; pinned: boolean; onGo: () => void; onPin: () => void }) {
  const hold = useRef<number | null>(null);
  const held = useRef(false);
  const Icon = space.icon;
  return (
    <li className="nw-tile-cell">
      <button
        type="button"
        className={cn('nw-space-tile', current && 'nw-space-tile-current')}
        data-mode={space.mode}
        style={{ ['--h' as string]: space.hue }}
        aria-current={current ? 'page' : undefined}
        aria-label={`${space.label}${current ? ', current space' : ''}`}
        onPointerDown={() => {
          held.current = false;
          if (space.id === 'home') return;
          hold.current = window.setTimeout(() => {
            held.current = true;
            navigator.vibrate?.(8);
            onPin();
          }, 520);
        }}
        onPointerUp={() => hold.current && window.clearTimeout(hold.current)}
        onPointerLeave={() => hold.current && window.clearTimeout(hold.current)}
        onClick={() => !held.current && onGo()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span className="nw-space-tile-icon">
          <Icon className="size-8" />
        </span>
        <span className="nw-space-tile-label">{space.label}</span>
        <StatusLine space={space} />
      </button>
      {space.id !== 'home' ? (
        <button type="button" className={cn('nw-tile-pin', pinned && 'nw-tile-pin-on')} onClick={onPin} aria-pressed={pinned} aria-label={pinned ? `Unpin ${space.label} from the scrubber` : `Pin ${space.label} to the scrubber`}>
          {pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
        </button>
      ) : null}
    </li>
  );
}

/**
 * Every space at once, grouped by category, with search. Opened by tapping the scrubber,
 * swiping up from it, pinching in on Home, or ⌘J.
 */
export function SpacesGrid({ open, onOpenChange, spaces, currentId, onGo }: { open: boolean; onOpenChange: (o: boolean) => void; spaces: Space[]; currentId: string | null; onGo: (s: Space) => void }) {
  const [q, setQ] = useState('');
  const prefs = usePreferences();
  const update = useUpdatePreferences();
  const pinned = prefs.data?.dockModules ?? [];
  const fine = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const shown = spaces.filter((s) => !needle || `${s.label} ${s.category} ${s.description ?? ''}`.toLowerCase().includes(needle));
    const map = new Map<string, Space[]>();
    for (const s of shown) map.set(s.category, [...(map.get(s.category) ?? []), s]);
    return [...map.entries()];
  }, [spaces, q]);

  const togglePin = (s: Space) => {
    const next = pinned.includes(s.id) ? pinned.filter((p) => p !== s.id) : [...pinned, s.id].slice(0, 12);
    update.mutate(
      { dockModules: next.length ? next : null },
      {
        onSuccess: () => toast.info(pinned.includes(s.id) ? `${s.label} unpinned from the scrubber` : `${s.label} pinned to the scrubber`),
      },
    );
  };

  return (
    <D.Root
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setQ('');
      }}
    >
      <D.Portal>
        <D.Overlay className="nw-grid-overlay" />
        <D.Content aria-describedby={undefined} className="nw-grid" onOpenAutoFocus={(e) => !fine && e.preventDefault()}>
          <motion.div className="nw-grid-inner" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 380, damping: 32 }}>
            <div className="nw-grid-head">
              <D.Title className="nw-display text-[2.2rem] sm:text-[2.8rem]">Spaces</D.Title>
              <label className="nw-grid-search">
                <Search className="size-5 opacity-60" aria-hidden />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a space" aria-label="Find a space" autoFocus={fine} />
              </label>
              <D.Close className="nw-action-round" aria-label="Close">
                <X className="size-5" />
              </D.Close>
            </div>
            <p className="nw-grid-hint">Tap to go. Hold a space, or use its pin, to keep it on the scrubber.</p>
            <div className="nw-grid-sections">
              {groups.map(([cat, list]) => (
                <section key={cat} aria-label={cat}>
                  <h2 className="nw-label mb-3">{cat}</h2>
                  <ul className="nw-tile-grid">
                    {list.map((s) => (
                      <SpaceTile
                        key={s.id}
                        space={s}
                        current={s.id === currentId}
                        pinned={pinned.includes(s.id)}
                        onGo={() => {
                          onOpenChange(false);
                          setQ('');
                          onGo(s);
                        }}
                        onPin={() => togglePin(s)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
            {!groups.length ? <p className="mt-10 text-[var(--nw-quiet)]">No space matches “{q}”.</p> : null}
          </motion.div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
